import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext/AuthContext'
import { useModal } from '../../../contexts/ModalContext/ModalContext'
import { auth } from '../../../firebase/config/config'
import {
  Container,
  Header,
  BackButton,
  Title,
  Subtitle,
  PricingGrid,
  PricingCard,
  Popular,
  PlanName,
  PlanPrice,
  PriceAmount,
  PricePeriod,
  PlanDescription,
  FeaturesList,
  Feature,
  FeatureIcon,
  SelectButton,
  CurrentPlan,
  FreeCard,
  FreeTitle,
  FreeDescription,
  FreeFeatures,
  ComparisonSection,
  ComparisonTitle,
  ComparisonTable,
  TableHeader,
  TableRow,
  TableCell,
  CheckIcon,
  CrossIcon,
  Footer,
  FooterText,
  FooterLink,
} from './styled'

export const PricingPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { showError } = useModal()
  const [loading, setLoading] = useState<string | null>(null)

  const handleSelectPlan = async (plan: 'standard' | 'premium') => {
    if (!user) {
      navigate('/login')
      return
    }

    setLoading(plan)

    try {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) {
        showError('Authentication error. Please sign in again.')
        return
      }

      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
        }/subscription/checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ plan }),
        },
      )

      const data = await response.json()

      if (data.success && data.data.url) {
        window.location.href = data.data.url
      } else {
        showError('Failed to create checkout session')
      }
    } catch (error) {
      console.error('Error creating checkout:', error)
      showError('Failed to create checkout session')
    } finally {
      setLoading(null)
    }
  }

  return (
    <Container>
      <Header>
        {user && (
          <BackButton onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </BackButton>
        )}
        <Title>Choose Your Plan</Title>
        <Subtitle>
          Start with our free plan or upgrade for more floorplans and features
        </Subtitle>
      </Header>

      <PricingGrid>
        <FreeCard>
          <FreeTitle>Free Plan</FreeTitle>
          <PlanPrice>
            <PriceAmount>$0</PriceAmount>
            <PricePeriod>/month</PricePeriod>
          </PlanPrice>
          <FreeDescription>
            Perfect for trying out QuickCasa Waypoint
          </FreeDescription>
          <FreeFeatures>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>1 floorplan
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              100 views per month
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              Basic sharing
            </Feature>
          </FreeFeatures>
          <CurrentPlan>Current Plan</CurrentPlan>
        </FreeCard>

        <PricingCard>
          <PlanName>Standard</PlanName>
          <PlanPrice>
            <PriceAmount>$20</PriceAmount>
            <PricePeriod>/month</PricePeriod>
          </PlanPrice>
          <PlanDescription>
            Great for small teams and businesses
          </PlanDescription>
          <FeaturesList>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              Up to 50 floorplans
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              10,000 included views/month
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              Share & collaborate
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              Custom branding
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              Export capabilities
            </Feature>
            <Feature>
              <FeatureIcon>+</FeatureIcon>
              $1 per additional floorplan
            </Feature>
            <Feature>
              <FeatureIcon>+</FeatureIcon>
              $0.25 per 100 extra views
            </Feature>
          </FeaturesList>
          <SelectButton
            onClick={() => handleSelectPlan('standard')}
            disabled={loading !== null}>
            {loading === 'standard' ? 'Loading...' : 'Select Standard'}
          </SelectButton>
        </PricingCard>

        <PricingCard $featured>
          <Popular>Most Popular</Popular>
          <PlanName>Premium</PlanName>
          <PlanPrice>
            <PriceAmount>$50</PriceAmount>
            <PricePeriod>/month</PricePeriod>
          </PlanPrice>
          <PlanDescription>Perfect for growing organizations</PlanDescription>
          <FeaturesList>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              Up to 75 floorplans
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              50,000 included views/month
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              All Standard features
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              Priority support
            </Feature>
            <Feature>
              <FeatureIcon>✓</FeatureIcon>
              Advanced analytics
            </Feature>
            <Feature>
              <FeatureIcon>+</FeatureIcon>
              $1 per additional floorplan
            </Feature>
            <Feature>
              <FeatureIcon>+</FeatureIcon>
              $0.25 per 100 extra views
            </Feature>
          </FeaturesList>
          <SelectButton
            $featured
            onClick={() => handleSelectPlan('premium')}
            disabled={loading !== null}>
            {loading === 'premium' ? 'Loading...' : 'Select Premium'}
          </SelectButton>
        </PricingCard>
      </PricingGrid>

      <ComparisonSection>
        <ComparisonTitle>Compare Plans</ComparisonTitle>
        <ComparisonTable>
          <TableHeader>
            <TableCell $header>Feature</TableCell>
            <TableCell $header>Free</TableCell>
            <TableCell $header>Standard</TableCell>
            <TableCell $header $featured>
              Premium
            </TableCell>
          </TableHeader>
          <TableRow>
            <TableCell>Floorplans</TableCell>
            <TableCell>1</TableCell>
            <TableCell>50</TableCell>
            <TableCell $featured>75</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Monthly Views</TableCell>
            <TableCell>100</TableCell>
            <TableCell>10,000</TableCell>
            <TableCell $featured>50,000</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Sharing & Collaboration</TableCell>
            <TableCell>
              <CheckIcon>✓</CheckIcon>
            </TableCell>
            <TableCell>
              <CheckIcon>✓</CheckIcon>
            </TableCell>
            <TableCell $featured>
              <CheckIcon>✓</CheckIcon>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Custom Branding</TableCell>
            <TableCell>
              <CrossIcon>✗</CrossIcon>
            </TableCell>
            <TableCell>
              <CheckIcon>✓</CheckIcon>
            </TableCell>
            <TableCell $featured>
              <CheckIcon>✓</CheckIcon>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Export Capabilities</TableCell>
            <TableCell>
              <CrossIcon>✗</CrossIcon>
            </TableCell>
            <TableCell>
              <CheckIcon>✓</CheckIcon>
            </TableCell>
            <TableCell $featured>
              <CheckIcon>✓</CheckIcon>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Priority Support</TableCell>
            <TableCell>
              <CrossIcon>✗</CrossIcon>
            </TableCell>
            <TableCell>
              <CrossIcon>✗</CrossIcon>
            </TableCell>
            <TableCell $featured>
              <CheckIcon>✓</CheckIcon>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Analytics</TableCell>
            <TableCell>Basic</TableCell>
            <TableCell>Standard</TableCell>
            <TableCell $featured>Advanced</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Additional Floorplans</TableCell>
            <TableCell>-</TableCell>
            <TableCell>$1/each</TableCell>
            <TableCell $featured>$1/each</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Additional Views</TableCell>
            <TableCell>-</TableCell>
            <TableCell>$0.25/100</TableCell>
            <TableCell $featured>$0.25/100</TableCell>
          </TableRow>
        </ComparisonTable>
      </ComparisonSection>

      <Footer>
        <FooterText>
          Need a custom plan for your enterprise?
          <FooterLink href="mailto:sales@quickcasa-waypoint.com">
            {' '}
            Contact us
          </FooterLink>
        </FooterText>
        <FooterText>
          All prices in USD. Cancel anytime.
          <FooterLink href="/terms"> Terms apply</FooterLink>
        </FooterText>
      </Footer>
    </Container>
  )
}
